import './login.screen.scss';
import {Stack, Box, Typography, Button, Divider} from '@mui/material';
import {useContext, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {AppContext} from '../../../contexts/app/context';
import {AppContextType} from '../../../contexts/app/types';
import {AuthContext} from '../../../contexts/auth/context';
import {AuthContextType} from '../../../contexts/auth/types';
import {translations} from '../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {SignInError} from '../../../enums/sign-in-error';
import {ScreenPaths} from '../../../enums/screen-path';
import {LegalLinksStack} from '../../../components/legal-links-stack/legal-links-stack';
import {SnackBarContextType} from '../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../contexts/snack-bar/context';
import {LoginScreenState} from '../../../interfaces/login-screen-state';
import {TextInput} from '../../../components/text-input/text-input';
import {PasswordInput} from '../../../components/password-input/password-input';
import LoadingButton from '@mui/lab/LoadingButton';
import {ReactComponent as LoginImage} from '../../../images/login.svg';
import {ProductLogoWithStick} from '../../../components/product-logo-with-stick/product-logo-with-stick';
import {QuickAccessManager} from '../../../managers/quick-access-manager';
import {MdLogin} from 'react-icons/md';
import {Environment} from '../../../enums';
import {ReactComponent as MicrosoftLogo} from '../../../logos/microsoft.svg';
import {ReactComponent as GoogleLogo} from '../../../logos/google.svg';
import {MainDatabase} from '../../../databases/main';
import {SessionDatabase} from '../../../databases/session';
import {exitToSocialApp} from '../../../utils/social-app/social-app-launch';

export interface LoginScreenProps {
  title: string;
  showIllustration?: boolean;
}

export default function LoginScreen({title, showIllustration = false}: LoginScreenProps) {
  // Get context
  const appContext: AppContextType = useContext(AppContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  // Translations
  const {t} = useTranslation();

  // Get history and location
  let history = useHistory();
  let {state} = useLocation<LoginScreenState>();
  const location = useLocation();

  // Hold states
  const [signingIn, setSigningIn] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);
  const [isQuickAccessLoginAvailable, setIsQuickAccessLoginAvailable] = useState<boolean>(false);

  // Validation
  useEffect(() => {
    // Password validation
    if (password.length > 0) {
      if (passwordError !== undefined) {
        // No errors
        setPasswordError(undefined);
      }
    } else {
      // Error
      if (showPasswordError) {
        setPasswordError(t(translations.login.errors.form_validation_invalid_field));
      }
    }

    // Email validation
    if (email.trim().length > 0) {
      if (emailError !== undefined) {
        // No errors
        setEmailError(undefined);
      }
    } else {
      // Error
      if (showEmailError) {
        setEmailError(t(translations.login.errors.form_validation_invalid_field));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, email, showEmailError, showPasswordError]);

  // Do login
  const handlePerformLogin = async (event: any) => {
    event.preventDefault();

    // Reset form errors
    setEmailError(undefined);
    setPasswordError(undefined);

    // Validate form
    let emailIsValid: boolean = true;
    let passwordIsValid: boolean = true;
    if (email.trim().length === 0) {
      emailIsValid = false;
    }
    if (password.length === 0) {
      passwordIsValid = false;
    }

    // Evaluate validation
    if (!emailIsValid || !passwordIsValid) {
      // Update UI
      setEmailError(
        !emailIsValid ? t(translations.login.errors.form_validation_invalid_field) : undefined
      );
      setPasswordError(
        !passwordIsValid ? t(translations.login.errors.form_validation_invalid_field) : undefined
      );

      // Stop execution here
      return;
    }

    // Everything looks ok, let's perform login. Start loading.
    setSigningIn(true);

    // Perform login
    const result: SignInError | undefined = await authContext.signIn(
      {email: email.trim(), password},
      true,
      // Show the relationship invite after the login if we have one.
      state && state.invite ? state.invite : undefined
    );

    // Evaluate result
    if (result) {
      // Display error. It could be an inline error or generic error.
      switch (result) {
        case SignInError.InvalidEmail:
          setEmailError(t(translations.login.errors[SignInError.InvalidEmail] as string));
          break;
        case SignInError.UserDisabled:
          setEmailError(t(translations.login.errors[SignInError.UserDisabled] as string));
          break;
        case SignInError.UserNotFound:
          setEmailError(t(translations.login.errors[SignInError.UserNotFound] as string));
          break;
        case SignInError.WrongPassword:
          setPasswordError(t(translations.login.errors[SignInError.WrongPassword] as string));
          break;
        default:
          // Set toast notification
          snackBarContext.setPublicToastNotification({
            message: t(translations.login.errors[result] as string),
            severity: 'error',
          });
          break;
      }

      // Stop loading
      setSigningIn(false);
    }
  };

  // Handle navigate to quick access
  const handleNavigateToQuickAccess = () => {
    // Get last part of the pathname
    const pathnameParts = location.pathname.split('/');
    const lastPart = pathnameParts[pathnameParts.length - 1];

    // Navigate
    history.push(ScreenPaths.LoginQuickAccess.replace(':navigationTarget', lastPart));
  };
  // On mount
  useEffect(() => {
    const checkQuickAccess = async () => {
      // Look at Local storage
      if (await QuickAccessManager.instance.isEnabled()) {
        // Go To Quick Access
        handleNavigateToQuickAccess();
      } else if (await QuickAccessManager.instance.isAvailable()) {
        // Go To Quick Access
        setIsQuickAccessLoginAvailable(true);
      }
    };

    // Clear Databases
    MainDatabase.instance.clearAllData();
    SessionDatabase.instance.clearAllData();

    checkQuickAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className="login-screen">
      <Box className={`login-screen-wrapper ${!showIllustration ? 'full-width' : ''}`}>
        <Box className="login-screen-container">
          <Box className="login-screen-header">
            <ProductLogoWithStick
              height={appContext.state.currentThemeSettings.loginLogoStickHeight}
            />
            {state && state.invite ? (
              <Typography variant="h6" className="login-screen-title">
                {t(translations.login.welcome_accept_invite)}
              </Typography>
            ) : (
              <>
                <Typography variant="h6" className="login-screen-title">
                  {t(title)}
                </Typography>
                <Typography variant="body1" className="login-screen-description">
                  {t(translations.login.instruction)}
                </Typography>
              </>
            )}
          </Box>
          <Box className="login-screen-content">
            <form onSubmit={handlePerformLogin}>
              <Box className="login-screen-email-container">
                <TextInput
                  label={t(translations.login.email_input)}
                  disabled={signingIn}
                  value={email}
                  handleChange={(value) => {
                    // Process value
                    let finalValue: string = '';
                    if (value !== undefined && value !== null) {
                      finalValue = value;
                    }

                    // Set email with the new value
                    setEmail(finalValue);
                  }}
                  handleBlur={() => setShowEmailError(true)}
                  error={emailError}
                  type="email"
                  autoComplete="username"
                  required={true}
                  color="primary"
                />
              </Box>
              <PasswordInput
                label={t(translations.login.password_input)}
                value={password}
                handleBlur={() => setShowPasswordError(true)}
                handleChange={(value) => {
                  let finalValue: string = '';
                  if (value !== undefined && value !== null) {
                    finalValue = value;
                  }

                  // Set password with the new value
                  setPassword(finalValue);
                }}
                error={passwordError}
                autocomplete="current-password"
                disabled={signingIn}
                required={true}
                color="primary"
              />
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={signingIn}
                className="login-screen-login-button"
                size="large"
              >
                {t(translations.login.enter_button)}
              </LoadingButton>

              {appContext.state.runningFromSocialApp && (
                <LoadingButton
                  className="login-screen-login-button"
                  sx={{
                    marginTop: '12px !important',
                    boxShadow: 'inset 0 0 0 4px rgba(255, 255, 255, 0.6)',
                  }}
                  variant="contained"
                  color="error"
                  loading={signingIn}
                  size="large"
                  onClick={() => exitToSocialApp()}
                >
                  {t(translations.office.generics.exit)}
                </LoadingButton>
              )}

              {appContext.state.configuration.environment === Environment.Development && (
                <Box
                  sx={{
                    marginTop: '16px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    disabled={signingIn}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      marginLeft: '12px',
                      marginRight: '12px',
                      maxWidth: '252px',
                      width: '100%',
                    }}
                    onClick={() => {
                      // Perform login
                      authContext
                        .signInWithMicrosoft(state && state.invite ? state.invite : undefined)
                        .catch((error) => {
                          if (error.code === 'auth/popup-closed-by-user') {
                            // This error is thrown when the user closes the popup window but with a delay of 8 seconds.
                            // This is a known issue with Firebase SDK:
                            // https://github.com/firebase/firebase-js-sdk/issues/8061
                            console.debug('Microsoft Login: Popup window closed by user');
                          } else {
                            console.error('Microsoft Sign-in failed', error);
                          }
                        });
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        marginRight: '12px',
                      }}
                    >
                      <MicrosoftLogo fontSize={22} />
                    </Box>
                    <Box>{t(translations.login.enter_button_microsoft)}</Box>
                  </Button>
                  <Button
                    disabled={signingIn}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      marginTop: '12px',
                      marginLeft: '12px',
                      marginRight: '12px',
                      maxWidth: '252px',
                      width: '100%',
                    }}
                    onClick={() => {
                      // Perform login
                      authContext
                        .signInWithGoogle(state && state.invite ? state.invite : undefined)
                        .catch((error) => {
                          if (error.code === 'auth/popup-closed-by-user') {
                            // This error is thrown when the user closes the popup window but with a delay of 8 seconds.
                            // This is a known issue with Firebase SDK:
                            // https://github.com/firebase/firebase-js-sdk/issues/8061
                            console.debug('Google Login: Popup window closed by user');
                          } else {
                            console.error('Google Sign-in failed', error);
                          }
                        });
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        marginRight: '12px',
                        svg: {
                          width: '22px',
                          height: '22px',
                        },
                      }}
                    >
                      <GoogleLogo fontSize={22} />
                    </Box>
                    <Box>{t(translations.login.enter_button_google)}</Box>
                  </Button>
                </Box>
              )}
            </form>
            <Button
              className="login-screen-forgot-password-button"
              disabled={signingIn}
              variant="text"
              color="inherit"
              size="medium"
              onClick={() => history.push(ScreenPaths.RecoverPassword)}
            >
              {t(translations.login.forgot_password)}
            </Button>
            {isQuickAccessLoginAvailable && (
              <>
                <Divider
                  className="divider"
                  sx={{
                    marginTop: '16px',
                  }}
                />
                <Box display="flex" className="login-screen-quick-access-container">
                  <MdLogin />
                  <Button
                    disabled={signingIn}
                    className="login-screen-quick-access-button"
                    variant="text"
                    color="inherit"
                    size="medium"
                    onClick={handleNavigateToQuickAccess}
                  >
                    {t(translations.login.quick_access)}
                  </Button>
                </Box>
              </>
            )}
            {!state?.invite && (
              <>
                <Stack className="login-screen-invite-container">
                  <Typography variant="body1" className="login-screen-invite-title">
                    {t(translations.login.validate_invite)}
                  </Typography>
                  <Button
                    className="login-screen-invite-button"
                    disabled={signingIn}
                    onClick={() => history.push(ScreenPaths.ValidateInvite)}
                    variant="text"
                    color="inherit"
                    size="medium"
                  >
                    {t(translations.login.use_invite_code)}
                  </Button>
                </Stack>
              </>
            )}
          </Box>
          <LegalLinksStack />
        </Box>
      </Box>
      {showIllustration && (
        <Box className="login-screen-image-wrapper">
          <LoginImage />
        </Box>
      )}
    </Box>
  );
}
